import {Action} from 'redux'
import {RolePermissionsModel, UserModel} from '../models/UserModel'
import {actionTypes} from './constants'

export interface ActionWithPayload<T> extends Action {
  payload?: T
}

// Auth state is memory-only: the session lives in the API's HttpOnly cookie and AuthInit
// rebuilds this from /profile on every page load. Drop what older builds persisted.
localStorage.removeItem('persist:auth')

const initialAuthState: IAuthState = {
  loading: false,
  success: false,
  user: undefined,
  isLoggedIn: false,
  data: {},
  userId: undefined,
  firstLogin: false,
  setNewPasswordSuccess: false,
  resetNewPasswordSuccess: false,
  module: undefined,
  rolePermissions: undefined,
}

export interface IAuthState {
  loading?: boolean
  success?: boolean
  user?: UserModel
  is2FALogin?: boolean
  isLoggedIn?: boolean
  data?: any
  userId?: string
  firstLogin?: boolean
  setNewPasswordSuccess?: boolean
  resetNewPasswordSuccess?: boolean
  resendOtpSuccess?: boolean
  module?: any
  rolePermissions?: RolePermissionsModel
}

export const reducer = (
  state: IAuthState = initialAuthState,
  action: ActionWithPayload<IAuthState>
) => {
  switch (action.type) {
    case actionTypes.LOGIN_REQUEST: {
      return {...state, loading: true, isLoggedIn: false}
    }

    case actionTypes.LOGIN_SUCCESS: {
      const isLoggedIn = !!action.payload?.isLoggedIn
      return {...state, is2FALogin: !isLoggedIn, isLoggedIn, loading: false}
    }

    case actionTypes.LOGIN_FINISH: {
      return {...state, loading: false}
    }

    case actionTypes.OTP_REQUEST: {
      return {...state, loading: true}
    }

    case actionTypes.OTP_SUCCESS: {
      const isLoggedIn = !!action.payload?.isLoggedIn
      return {...state, is2FALogin: false, isLoggedIn, loading: true}
    }

    case actionTypes.OTP_FINISH: {
      return {...state, loading: false}
    }

    case actionTypes.FIRST_LOGIN_REQUEST: {
      return {...state, loading: true}
    }
    case actionTypes.FIRST_LOGIN_SUCCESS: {
      const userId = action.payload?.userId
      const firstLogin = action.payload?.firstLogin
      return {...state, data: action.payload, userId, firstLogin, loading: false}
    }

    case actionTypes.FIRST_LOGIN_FINISH: {
      return {...state, loading: false}
    }

    case actionTypes.SET_PASSWORD_REQUEST: {
      return {...state, loading: true, setNewPasswordSuccess: false}
    }

    case actionTypes.SET_PASSWORD_SUCCESS: {
      return {
        ...state,
        data: action.payload,
        firstLogin: false,
        loading: false,
        setNewPasswordSuccess: true,
      }
    }

    case actionTypes.SET_PASSWORD_FINISH: {
      return {...state, loading: false, setNewPasswordSuccess: false}
    }

    case actionTypes.RESET_PASSWORD_REQUEST: {
      return {...state, loading: true, resetNewPasswordSuccess: false}
    }

    case actionTypes.RESET_PASSWORD_SUCCESS: {
      return {
        ...state,
        data: action.payload,
        loading: false,
        resetNewPasswordSuccess: true,
      }
    }

    case actionTypes.RESET_PASSWORD_FINISH: {
      return {...state, loading: false, resetNewPasswordSuccess: false}
    }

    case actionTypes.FORGOT_PASSWORD_REQUEST: {
      return {...state, loading: true}
    }

    case actionTypes.FORGOT_PASSWORD_SUCCESS: {
      return {...state, data: action.payload, success: true, loading: false}
    }

    case actionTypes.FORGOT_PASSWORD_FINISH: {
      return {...state, data: {}, success: false, loading: false}
    }

    case actionTypes.RESEND_OTP_FINISH: {
      return {...state, data: {}, success: false, loading: false}
    }

    case actionTypes.RESEND_OTP_REQUEST: {
      return {...state, loading: true}
    }

    case actionTypes.RESEND_OTP_SUCCESS: {
      return {...state, data: action.payload, success: true, loading: false}
    }

    case actionTypes.Logout: {
      return {...initialAuthState, module: undefined}
    }

    case actionTypes.UserLoaded: {
      const user = action.payload?.user
      return {...state, user, isLoggedIn: !!user, rolePermissions: user?.data?.role}
    }

    case actionTypes.SetUser: {
      const user = action.payload?.user
      return {...state, user}
    }

    case actionTypes.GET_DB_MODULE_REQUEST: {
      return {...state, loading: true}
    }

    case actionTypes.GET_DB_MODULE_SUCCESS: {
      return {...state, module: action.payload, loading: false}
    }

    case actionTypes.GET_DB_MODULE_FINISH: {
      return {...state, loading: false}
    }

    default:
      return state
  }
}
